import React from 'react';
import { Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { Camera, KeyRound, Pencil, Trash2 } from 'lucide-react';
import ActionsMenu from '../ActionsMenu';
import PhotoGallery from '../PhotoGallery';
import { Lease, formatInterval } from '../../types/leases';
import { PhotoModel } from '../../types/protocols';
import { formatCurrency } from '../../Helpers/FormatUtils';
import { leaseStatus, leaseStatusLabel } from '../../Helpers/leaseUtils';
import './RentableDetail.css';

/*
 * Shared pieces of the detail modals for rentable objects (parking spot,
 * storage unit, apartment/lokal): title with status and ⋯ menu, sections with
 * the app's section titles, the Hyresavtal card and the Foton section.
 */

interface TitleProps {
    name: string;
    /** Ledig/Uthyrd pill; absent for objects that are not rented out. */
    status?: { rented: boolean; label: string };
    /** Admins only; the menu renders right-aligned next to the close button. */
    onEdit?: () => void;
    onDelete?: () => void;
    deleteLabel: string;
    disabled: boolean;
    /** Small buttons left of the ⋯ menu (Nycklar). */
    actions?: React.ReactNode;
}

/** Contents of `Modal.Header`: the name grows, so the ⋯ menu sits on the right beside the close button. */
export const RentableDetailTitle: React.FC<TitleProps> = ({ name, status, onEdit, onDelete, deleteLabel, disabled, actions }) => {
    const items = [
        ...(onEdit ? [{ label: 'Redigera', icon: <Pencil size={18} color="#6c757d" />, onClick: onEdit }] : []),
        ...(onDelete ? [{ label: deleteLabel, icon: <Trash2 size={18} />, danger: true, onClick: onDelete }] : []),
    ];
    return (
        <>
            <div className="rentable-detail-title">
                <h5 className="modal-title">{name}</h5>
                {status && (
                    <span className={`rentable-detail-status ${status.rented ? 'rentable-detail-status--rented' : 'rentable-detail-status--free'}`}>
                        {status.label}
                    </span>
                )}
            </div>
            {actions && <div className="rentable-detail-header-actions">{actions}</div>}
            {items.length > 0 && (
                <div className="rentable-detail-menu">
                    <ActionsMenu disabled={disabled} items={items} />
                </div>
            )}
        </>
    );
};


interface SectionProps {
    icon: React.ReactNode;
    title: string;
    count?: number;
    /** Right-aligned in the title row (e.g. "Lägg till foton"). */
    action?: React.ReactNode;
    children: React.ReactNode;
}

export const RentableDetailSection: React.FC<SectionProps> = ({ icon, title, count, action, children }) => (
    <section className="rentable-detail-section">
        <div className="rentable-detail-section-row">
            <h6 className="rentable-detail-section-title">
                {icon}{title}{count !== undefined && count > 0 && <span className="rentable-detail-section-count">{count}</span>}
            </h6>
            {action}
        </div>
        {children}
    </section>
);

interface LeaseSectionProps {
    rented: boolean;
    lease?: Lease;
    tenantName?: string;
    leasesLink?: string;
    /** "Uthyrd"/"Uthyrt" while the lease is still loading. */
    rentedText: string;
    /** E.g. "Platsen är ledig." */
    vacantText: string;
    /** Nytt hyresavtal; admins only. */
    onNewLease?: () => void;
    submitting: boolean;
    /** Row of links under the card (Tidigare hyresgäster, queue links). */
    links?: React.ReactNode;
    /** Shown right under the card, e.g. the queue question after Nytt hyresavtal. */
    children?: React.ReactNode;
}

export const RentableLeaseSection: React.FC<LeaseSectionProps> = ({
    rented, lease, tenantName, leasesLink, rentedText, vacantText, onNewLease, submitting, links, children
}) => (
    <RentableDetailSection icon={<KeyRound size={16} />} title="Hyresavtal">
        <div className="rentable-detail-lease-card">
            {rented ? (
                lease ? (
                    <div>
                        <div><strong>{tenantName}</strong> · {formatCurrency(lease.amount)} {formatInterval(lease.interval)}</div>
                        <div className="text-muted">
                            {leaseStatusLabel(leaseStatus(lease))} · {lease.startDate}{lease.endDate ? ` – ${lease.endDate}` : ' – tills vidare'}
                        </div>
                    </div>
                ) : (
                    <span className="text-muted">{rentedText}</span>
                )
            ) : (
                <span className="text-muted">{vacantText}</span>
            )}
            {rented && leasesLink && <Link to={leasesLink} className="btn btn-outline-secondary btn-sm">Visa avtal</Link>}
            {!rented && onNewLease && (
                <Button variant="outline-primary" size="sm" onClick={onNewLease} disabled={submitting}>Nytt hyresavtal</Button>
            )}
        </div>
        {children}
        {links && <div className="rentable-detail-links">{links}</div>}
    </RentableDetailSection>
);

interface PhotosSectionProps {
    photos: { [key: string]: PhotoModel };
    buildingKey: string;
}

/** Read-only; photos are added and removed in the object's Redigera form. */
export const RentablePhotosSection: React.FC<PhotosSectionProps> = ({ photos, buildingKey }) => {
    const photoCount = Object.keys(photos).length;
    return (
        <RentableDetailSection icon={<Camera size={16} />} title="Foton" count={photoCount}>
            {photoCount > 0
                ? <PhotoGallery photos={photos} buildingKey={buildingKey} thumbnailSize={90} />
                : <p className="text-muted mb-0">Inga foton</p>}
        </RentableDetailSection>
    );
};
